import React, { useEffect, useRef, useState } from 'react';
import ButtonWithLoading from '../common/ButtomWIthLoading';
import DisplayInfoMessage from '../common/DisplayInfoMessage';
import { AUTH_UI_TEXT } from '../../constants/auth.constants';

interface TfaProps {
  email: string;
  onVerify: (code: string) => void;
  onBackToLogin: () => void;
  isLoading?: boolean;
  displayMessage?: { error: boolean; message: string };
}

const Tfa: React.FC<TfaProps> = ({
  email,
  onVerify,
  onBackToLogin,
  isLoading = false,
  displayMessage = { error: false, message: '' },
}) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Get client type for dynamic styling
  const clientType = process.env.REACT_APP_CLIENT;
  const primaryColor = clientType === 'salesbot2290ai' ? '#16A34A' : '#114175';
  const hoverColor = clientType === 'salesbot2290ai' ? '#15803D' : '#6491C3';
  const focusBorderColor = clientType === 'salesbot2290ai' ? 'focus:border-[#16A34A]' : 'focus:border-[#114175]';

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleCodeInputChange = (index: number, value: string) => {
    if (value.length > 1 || !/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = pastedData.padEnd(6, '').split('');
    setCode(newCode);

    const nextEmptyIndex = newCode.findIndex((digit) => digit === '');
    const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : 5;
    inputRefs.current[focusIndex]?.focus();
  };

  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      const tfaCode = code.join('');
      if (tfaCode.length === 6) handleVerify();
      return;
    }

    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = () => {
    const tfaCode = code.join('');
    if (tfaCode.length === 6) onVerify(tfaCode);
  };

  const isCodeComplete = code.join('').length === 6;

  return (
    <div className="p-4 sm:p-6 max-w-lg mx-auto w-full">
      {/* Header */}
      <div className="text-center mb-4 sm:mb-6">
        <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{AUTH_UI_TEXT.TFA_TITLE}</h3>
        <p className="text-sm text-gray-600">
          {AUTH_UI_TEXT.TFA_DESCRIPTION}
        </p>
        <p className="text-sm font-semibold text-gray-900 mt-1">{email}</p>
      </div>

      {/* 2FA Code Input Fields */}
      <div className="flex justify-center gap-2 sm:gap-3 mb-4">
        {code.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleCodeInputChange(index, e.target.value)}
            onKeyDown={(e) => handleCodeKeyDown(index, e)}
            onPaste={handlePaste}
            className={`w-10 h-10 sm:w-12 sm:h-12 text-center text-lg font-semibold border-2 border-gray-300 rounded-lg ${focusBorderColor} focus:outline-none focus:ring-2 ${
              clientType === 'salesbot2290ai' ? 'focus:ring-[#16A34A]' : 'focus:ring-[#114175]'
            } transition-all ${isLoading ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
            disabled={isLoading}
          />
        ))}
      </div>

      {/* Error/Success Message */}
      <DisplayInfoMessage displayMessage={displayMessage} />

      {/* Verify Button */}
      <ButtonWithLoading
        isLoading={isLoading}
        label={AUTH_UI_TEXT.TFA_BUTTON}
        type="button"
        className={`w-full py-3 rounded-full text-white font-semibold text-base transition-colors ${
          isCodeComplete && !isLoading
            ? 'cursor-pointer'
            : 'bg-gray-300 cursor-not-allowed'
        }`}
        style={isCodeComplete && !isLoading ? { backgroundColor: primaryColor } : {}}
        onMouseEnter={(e: any) => {
          if (isCodeComplete && !isLoading) {
            e.currentTarget.style.backgroundColor = hoverColor;
          }
        }}
        onMouseLeave={(e: any) => {
          if (isCodeComplete && !isLoading) {
            e.currentTarget.style.backgroundColor = primaryColor;
          }
        }}
        disabled={!isCodeComplete || isLoading}
        onClick={handleVerify}
      />

      {/* Help Text */}
      <div className="text-center mt-4 sm:mt-6">
        <p className="text-xs text-gray-500">
          {AUTH_UI_TEXT.TFA_HELP_TEXT}
        </p>
      </div>

      {/* Back to Login */}
      <div className="text-center mt-4">
        <button
          type="button"
          className="text-sm text-gray-600 hover:text-gray-900 hover:underline"
          onClick={onBackToLogin}
        >
          {AUTH_UI_TEXT.TFA_BACK_TO_SIGNUP}
        </button>
      </div>
    </div>
  );
};

export default Tfa;

